import { Button, Divider, Drawer, Stack, Typography } from '@mui/material'
import {
  type ResponseNumber,
  type ResponseNumberDecimalPoint,
  ResponseNumberSubTypeEnum,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import { type FormikHelpers, useFormik } from 'formik'
import type { FC } from 'react'
import {
  isNumberRuleOutOfRange,
  numberRuleOutOfRangeMessage,
} from 'src/constants/responseNumber'
import FractionalDigitsRow, {
  MAX_NUMBER_FRACTIONAL_DIGITS,
} from './FractionalDigitsRow'
import NormalValueRow from './NormalValueRow'

interface OwnProps {
  isOpen: boolean
  questionNode: TemplateNodeSchema
  response?: ResponseNumber
  onClose: () => void
  onSave: (questionPatch: Partial<TemplateQuestion>) => void
}

export type NumberRuleProps = {
  hasNumberRule: boolean
  minimum?: number
  maximum?: number
  scale?: string
  subType?: ResponseNumberSubTypeEnum
  decimalPoint?: ResponseNumberDecimalPoint
}

const EditResponseNumberRuleDrawerContainer: FC<OwnProps> = ({
  response,
  questionNode,
  onClose,
  isOpen,
  onSave,
}) => {
  // 数値タイプの質問にルールを設定する
  const changeResponseNumberRule = (
    scale: string,
    newResponse: Pick<
      ResponseNumber,
      'minimum' | 'maximum' | 'subType' | 'decimalPoint'
    >,
  ) => {
    onSave({ scale, responseNumbers: [newResponse] })
  }

  // subType が 0 である場合にもルールなしの判定としたいが、
  // 0 は Enum に定義されておらず型エラーが発生するため、falsy かどうかで判定する
  const hasNumberRule = !!response?.subType

  const initialValues: NumberRuleProps = {
    hasNumberRule,
    minimum: response?.minimum ?? undefined,
    maximum: response?.maximum ?? undefined,
    scale: questionNode.question?.scale,
    subType: response?.subType,
    decimalPoint: {
      value: response?.decimalPoint?.value,
      isActive: response?.decimalPoint?.isActive,
    },
  }

  const formikProps = useFormik({
    initialValues: initialValues,
    validate: ({
      hasNumberRule,
      subType,
      minimum,
      maximum,
      decimalPoint,
    }: NumberRuleProps) => {
      const errors: Partial<Record<keyof NumberRuleProps, string>> = {}

      if (hasNumberRule) {
        if (!subType) {
          errors.subType = 'ルールの設定を行ってください'
          return errors
        }

        switch (subType) {
          case ResponseNumberSubTypeEnum.BETWEEN:
            if (minimum === undefined || minimum === null) {
              errors.minimum = '最小値を入力してください'
              return errors
            }
            if (Number.isNaN(minimum)) {
              errors.minimum = '最小値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(minimum)) {
              errors.minimum = numberRuleOutOfRangeMessage('最小値')
              return errors
            }
            if (maximum === undefined || maximum === null) {
              errors.maximum = '最大値を入力してください'
              return errors
            }
            if (Number.isNaN(maximum)) {
              errors.maximum = '最大値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(maximum)) {
              errors.maximum = numberRuleOutOfRangeMessage('最大値')
              return errors
            }
            if (minimum > maximum) {
              errors.minimum = '最小値 ー 最大値 で入力してください'
              return errors
            }
            break
          case ResponseNumberSubTypeEnum.LESS_THAN:
            if (maximum === undefined || maximum === null) {
              errors.maximum = '最大値を入力してください'
              return errors
            }
            if (Number.isNaN(maximum)) {
              errors.maximum = '最大値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(maximum)) {
              errors.maximum = numberRuleOutOfRangeMessage('最大値')
              return errors
            }
            break
          case ResponseNumberSubTypeEnum.GREATER_THAN:
            if (minimum === undefined || minimum === null) {
              errors.minimum = '最小値を入力してください'
              return errors
            }
            if (Number.isNaN(minimum)) {
              errors.minimum = '最小値を数値で入力してください'
              return errors
            }
            if (isNumberRuleOutOfRange(minimum)) {
              errors.minimum = numberRuleOutOfRangeMessage('最小値')
              return errors
            }
            break
        }
      }

      if (decimalPoint) {
        const { isActive, value } = decimalPoint
        if (isActive && value === undefined) {
          errors.decimalPoint = '小数点以下の桁数を入力してください。'
          return errors
        }
        if (
          isActive &&
          value !== undefined &&
          value > MAX_NUMBER_FRACTIONAL_DIGITS
        ) {
          errors.decimalPoint = `小数点以下の桁数は${MAX_NUMBER_FRACTIONAL_DIGITS}以下で入力してください。`
          return errors
        }
        if (!!subType && isActive && value !== undefined) {
          const maximumDecimalPoint = String(maximum).split('.')[1]?.length || 0
          const minimumDecimalPoint = String(minimum).split('.')[1]?.length || 0
          const ruleDecimalPoint =
            maximumDecimalPoint > minimumDecimalPoint
              ? maximumDecimalPoint
              : minimumDecimalPoint
          if (value < ruleDecimalPoint) {
            errors.decimalPoint =
              '小数点以下の桁数をルールの小数点桁数より大きい数値で入力してください。'
            return errors
          }
        }
      }

      return errors
    },
    onSubmit: (
      { subType, minimum, maximum, decimalPoint, scale }: NumberRuleProps,
      { resetForm }: FormikHelpers<NumberRuleProps>,
    ) => {
      if (subType === ResponseNumberSubTypeEnum.GREATER_THAN)
        maximum = undefined
      if (subType === ResponseNumberSubTypeEnum.LESS_THAN) minimum = undefined

      changeResponseNumberRule(scale ?? '', {
        minimum,
        maximum,
        subType,
        decimalPoint,
      })
      onClose()
      resetForm()
    },
    enableReinitialize: true,
  })

  const { handleSubmit, resetForm } = formikProps

  const onCloseOrCancel = () => {
    onClose()
    resetForm()
  }

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return
        onCloseOrCancel()
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '100%',
            width: '100%',
            maxWidth: 600,
          },
        },
      }}
    >
      <Stack sx={{ minHeight: '100vh' }}>
        <Typography variant="h6" sx={{ my: 2, px: 3 }}>
          ルールの設定
        </Typography>
        <Divider />
        <Stack sx={{ p: 2 }} spacing={2}>
          <NormalValueRow {...{ formikProps }} />
          <FractionalDigitsRow {...{ formikProps }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button variant="text" onClick={onCloseOrCancel}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={() => handleSubmit()}>
              保存
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  )
}

export default EditResponseNumberRuleDrawerContainer
